<template>
  <div>
    <div class="flex form_group relative text-black">
      <button
        type="button"
        @click="$modal.show('modal-select-country')"
        class="absolute left-1 z-10 flex items-center gap-1 cursor-pointer hover:opacity-80"
      >
        <img
          :src="getFlag(activeNum.code)"
          alt=""
          class="object-contain w-8 h-8"
        />
        <div class="text-sm pointer-events-none">{{ activeNum.dial_code }}</div>
        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>

      <the-mask
        :mask="inputMask.replaceAll('_', '#')"
        :tokens="customTokens"
        :masked="true"
        :placeholder="inputMask"
        v-model="localPhone"
        style="width: 100%; border-radius: 0px 8px 8px 0px; padding-left: 110px; font-size: 14px; font-family: sans-serif;"
        name="phone"
        id="number"
        type="tel"
      />
    </div>

    <modal class="input-modal" name="modal-select-country" :width="350" :height="'auto'" scrollable>
      <div class="text-center bg-white py-4 px-2 relative">
        <div class="right-0 top-0 p-2 absolute">
          <button type="button" @click="$modal.hide('modal-select-country')" class="text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="flex items-center w-full px-2 mt-6 bg-white">
          <div class="relative w-full">
            <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
              <svg aria-hidden="true" class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </div>
            <input
              type="search"
              @input="onSearch"
              class="block px-4 py-2 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300"
              style="padding-left: 35px;"
              placeholder=""
            />
          </div>
        </div>

        <div class="pt-4 px-2 h-[50vh] overflow-auto">
          <ul style="padding: 0">
            <li
              v-for="item in filteredNumbers"
              :key="item.code"
              @click="selectCountry(item)"
              class="flex items-center justify-between py-4 px-2 border-b hover:bg-gray-100 cursor-pointer"
            >
              <div class="flex items-center gap-2">
                <img :src="getFlag(item.code)" class="w-5 h-5" alt="" />
                <span class="text-left">{{ item.name }}</span>
              </div>
              <span class="text-[#001345] font-bold">{{ item.dial_code }}</span>
            </li>
          </ul>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import NUMBERS from "./utils/numbers.json";

export default {
  name: "AppInputPhoneNumber",

  props: {
    phoneNumber: {
      type: String,
      default: ""
    },
    propertyId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      activeNum: {
        code: "US",
        dial_code: "+1",
        mask: "___-___-____"
      },
      searchQuery: "",
      customTokens: {
        "#": { pattern: /\d/ },
        Y: { pattern: /[0-9]/ },
        X: { pattern: /[0-9a-zA-Z]/ },
        S: { pattern: /[a-zA-Z]/ },
        A: { pattern: /[a-zA-Z]/, transform: v => v.toLocaleUpperCase() },
        a: { pattern: /[a-zA-Z]/, transform: v => v.toLocaleLowerCase() },
        "!": { escape: true }
      }
    };
  },

  computed: {
    inputMask() {
      return this.activeNum.mask || "___-___-____";
    },

    filteredNumbers() {
      if (!this.searchQuery) return NUMBERS;
      const q = this.searchQuery.toLowerCase();
      return NUMBERS.filter(
        n => n.name.toLowerCase().includes(q) || n.dial_code.includes(q)
      );
    },

    // v-model target for the-mask.
    // getter: strips dial code from phoneNumber prop for display
    // setter: rebuilds full number and emits up + saves to localStorage
    localPhone: {
      get() {
        if (!this.phoneNumber) return "";
        const matched = this.findDialCode(this.phoneNumber);
        if (matched) {
          return this.stripDialCode(this.phoneNumber, matched.dial_code);
        }
        return this.cleanDigits(this.phoneNumber);
      },
      set(val) {
        const digits = this.cleanDigits(val);
        const full = this.activeNum.dial_code + digits;
        this.$emit("update:phoneNumber", full);
        this.$root.setPropertyToLocalStorage(this.propertyId, full);
      }
    }
  },

  watch: {
    // Sync activeNum when parent pushes a phoneNumber with a different dial code
    // e.g. fetchPerson fills in a number from a different country
    phoneNumber: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;
        const matched = this.findDialCode(newVal);
        if (matched && matched.code !== this.activeNum.code) {
          this.activeNum = matched;
          this.saveCountryMask(matched);
        }
      }
    }
  },

  created() {
    this.initCountry();
  },

  methods: {
    initCountry() {
      // Priority 1: saved localStorage mask
      const savedMask = localStorage.getItem("phone-number-mask");
      if (savedMask) {
        const normalize = s => (s || "").replace(/[^\d+]/g, "");
        const prefix = normalize(savedMask);
        const found = NUMBERS
          .slice()
          .sort((a, b) => normalize(b.dial_code).length - normalize(a.dial_code).length)
          .find(n => prefix.startsWith(normalize(n.dial_code)));
        if (found) {
          this.activeNum = found;
          return;
        }
      }

      // Priority 2: phone number prop already has a dial code
      if (this.phoneNumber) {
        const matched = this.findDialCode(this.phoneNumber);
        if (matched) {
          this.activeNum = matched;
          this.saveCountryMask(matched);
          return;
        }
      }

      // Priority 3: hotel ext_data or server env
      try {
        let countryCode = "+1";
        const extData = this.$root.hotel?.ext_data;

        if (extData?.country_code) {
          countryCode = extData.country_code;
        } else {
          const server = (process.env.VUE_APP_SERVER || "").toLowerCase();
          if (server === "ru") countryCode = "+7";
          else if (server === "eu") countryCode = "+48";
          else if (server === "uk") countryCode = "+44 (0)";
        }

        if (countryCode.includes("+44")) countryCode = "+44 (0)";

        const found = NUMBERS.find(n => n.dial_code === countryCode);
        if (found) {
          this.activeNum = found;
          this.saveCountryMask(found);
        }
      } catch (e) {
        console.error("[AppInputPhoneNumber] initCountry failed:", e);
      }
    },

    selectCountry(item) {
      this.activeNum = item;
      this.saveCountryMask(item);
      this.$modal.hide("modal-select-country");
      // Reset phone to just the dial code after country change
      this.$emit("update:phoneNumber", item.dial_code);
      this.$root.setPropertyToLocalStorage(this.propertyId, item.dial_code);
    },

    onSearch(e) {
      this.searchQuery = e.target.value;
    },

    getFlag(code) {
      try {
        return require(`@/assets/flags/${code}.svg`);
      } catch {
        return "";
      }
    },

    cleanDigits(val) {
      return (val || "").replace(/[\s\-\(\)]/g, "");
    },

    stripDialCode(phone, dialCode) {
      if (phone.startsWith(dialCode)) {
        return this.cleanDigits(phone.slice(dialCode.length));
      }
      return this.cleanDigits(phone);
    },

    findDialCode(phone) {
      return (
        NUMBERS.slice()
          .sort((a, b) => b.dial_code.length - a.dial_code.length)
          .find(n => phone.startsWith(n.dial_code)) || null
      );
    },

    saveCountryMask(item) {
      localStorage.setItem("phone-number-mask", item.dial_code + " " + item.mask);
    }
  }
};
</script>

<style>
.input-modal .vm--modal {
  position: absolute !important;
}
</style>